import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';

/**
 * Fetch all criteria product lines
 */
export const useProductLines = () => {
  return useQuery({
    queryKey: ['productLines'],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/product-lines`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (this data rarely changes)
  });
};

/**
 * Fetch a single criteria product line by ID
 */
export const useProductLine = productLineId => {
  return useQuery({
    queryKey: ['productLine', productLineId],
    queryFn: async () => {
      if (!productLineId) {
        throw new Error('Product Line ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/product-lines/${productLineId}`);
      return response.data;
    },
    enabled: !!productLineId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create a new criteria product line (admin only)
 */
export const useCreateProductLine = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async productLineData => {
      const response = await axios.post(`${HOST}/api/certification-tool/product-lines`, productLineData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productLines'] });
    },
  });
};

/**
 * Update a criteria product line (admin only)
 */
export const useUpdateProductLine = productLineId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async patchDocument => {
      const response = await axios.patch(`${HOST}/api/certification-tool/product-lines/${productLineId}`, patchDocument, {
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productLines'] });
      queryClient.invalidateQueries({ queryKey: ['productLine', productLineId] });
    },
  });
};

/**
 * Delete a criteria product line (admin only)
 */
export const useDeleteProductLine = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async productLineId => {
      await axios.delete(`${HOST}/api/certification-tool/product-lines/${productLineId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productLines'] });
    },
  });
};
